import {
  GaugeData,
  KeywordFilter,
  KeywordPerformanceGaugeData,
  MetricFormatterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { useMainEntityName, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import {
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName,
  useMetricFormatter
} from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import React from 'react';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { useScrollToId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useScrollToId';
import { useKeywordPerformanceFilterStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/KeywordPerformanceFilterStore';
import TextArrow from '../components/TextArrow';

const mask1 = '***';
const mask2 = '###';
const ClientLevelKeywordTileSubtitles = {
  [KeywordFilter.Usage]: `On average, there are ${mask1} keywords on each product page.`,
  [KeywordFilter.KeywordBenchmark]: `On average, each product page should have ${mask1} keywords.`,
  [KeywordFilter.LowUsage]: `View ${mask1} products with low usage`,
  [KeywordFilter.Productivity]: `On average, ${mask1} (of ${mask2}) keywords successfully generate traffic to each product page.`,
  [KeywordFilter.UnProductiveKeywords]: `On average, ${mask1} (of ${mask2}) keywords do not generate traffic to each product page.`,
  [KeywordFilter.LowProductivity]: `View ${mask1} products with low productivity`,
  [KeywordFilter.Popularity]: `On average, the ${mask1} keywords on each product page generate ${mask2} in weekly search volume.`,
  [KeywordFilter.PopularityBenchmark]: `An optimal keyword list should generate ${mask1} in weekly search volume.`,
  [KeywordFilter.LowPopularity]: `View ${mask1} products with low popularity`
};

const ProductLevelKeywordTileSubtitles = {
  [KeywordFilter.Usage]: `There are ${mask1} keywords on this product page.`,
  [KeywordFilter.KeywordBenchmark]: `There should be ${mask1} keywords on this product page.`,
  [KeywordFilter.Productivity]: `${mask1} (of ${mask2}) keywords successfully generate traffic to this product page.`,
  [KeywordFilter.UnProductiveKeywords]: `${mask1} (of ${mask2}) keywords do not generate traffic to this product page.`,
  [KeywordFilter.Popularity]: `The ${mask1} keywords generate ${mask2} in weekly search volume.`,
  [KeywordFilter.PopularityBenchmark]: `An optimal keyword list should generate ${mask1} in weekly search volume.`
};

// Only KeywordFilter.Usage, KeywordFilter.Productivity, KeywordFilter.UnProductiveKeywords,
// KeywordFilter.Popularity are used in the Spline Chart
const SplineChartSubtitleHeaders = {
  [KeywordFilter.Usage]: {
    default: 'keywords used',
    product: 'keywords used'
  }, // group 1 (Usage)
  [KeywordFilter.Productivity]: {
    default: 'productive keywords',
    product: 'productive keywords'
  }, // group 2 (Productivity)
  [KeywordFilter.UnProductiveKeywords]: {
    default: 'unproductive keywords',
    product: 'unproductive keywords'
  }, // group 2 (Productivity)
  [KeywordFilter.Popularity]: {
    default: 'keywords',
    product: 'keywords'
  } // group 3 (Popularity)
};

/**
 * Hook that provides functions for generating subtitles for content charts
 */
export const useKeywordSubtitleV3 = () => {
  const { isProductEntity } = useProductEntity();
  const metricFormatter = useMetricFormatter();
  const mainTimePeriodName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodName = useComparisonTimePeriodShortDisplayName();
  const entityName = useMainEntityName();
  const scrollToId = useScrollToId<typeof ContentSectionId>();
  const { setUsageBelow70, setPopularityBelow70, setProductivityBelow70, resetFilters } =
    useKeywordPerformanceFilterStore();

  const defaultMetricFormatter: MetricFormatterProps = {
    showFullValue: false,
    decimalPlaces: 0,
    valueMetricType: METRICTYPE.VOLUME
  };
  const generateSingleMetricSubtitle = (
    item: GaugeData,
    filterKey: KeywordFilter,
    metricFormatterProps: MetricFormatterProps = defaultMetricFormatter
  ) => {
    const formattedValue = metricFormatter(item.value, metricFormatterProps.valueMetricType, {
      showFullValue: metricFormatterProps.showFullValue,
      decimalPlaces: metricFormatterProps.decimalPlaces
    });
    if (isProductEntity) {
      return ProductLevelKeywordTileSubtitles[filterKey].replace(mask1, formattedValue);
    }
    return ClientLevelKeywordTileSubtitles[filterKey].replace(mask1, formattedValue);
  };

  const generateProductivitySubtitle = (fullData: KeywordPerformanceGaugeData, filterKey: KeywordFilter) => {
    const item = fullData.gaugeData.find((item) => item.filterKey === filterKey);
    const totalKeywords = fullData.mainScores.averageCountOfKeywords;
    const firstMetric = metricFormatter(item.value, METRICTYPE.DECIMAL, {
      showFullValue: false,
      decimalPlaces: isProductEntity ? 0 : 1
    });
    const secondMetric = metricFormatter(totalKeywords, METRICTYPE.DECIMAL, {
      showFullValue: false,
      decimalPlaces: isProductEntity ? 0 : 1
    });

    if (isProductEntity) {
      return ProductLevelKeywordTileSubtitles[filterKey].replace(mask1, firstMetric).replace(mask2, secondMetric);
    }
    return ClientLevelKeywordTileSubtitles[filterKey].replace(mask1, firstMetric).replace(mask2, secondMetric);
  };

  const generatePopularitySubtitle = (fullData: KeywordPerformanceGaugeData, filterKey: KeywordFilter) => {
    const item = fullData.gaugeData.find((item) => item.filterKey === filterKey);
    const totalKeywords =
      fullData.mainScores.averageCountOfKeywordsGeneratingTraffic +
      fullData.mainScores.averageUnproductiveKeywordsCount;

    const firstMetric = metricFormatter(totalKeywords, METRICTYPE.DECIMAL, {
      showFullValue: false,
      decimalPlaces: isProductEntity ? 0 : 1
    });
    const secondMetric = metricFormatter(item.value, METRICTYPE.VOLUME, {
      showFullValue: false,
      decimalPlaces: 1
    });

    if (isProductEntity) {
      return ProductLevelKeywordTileSubtitles[filterKey].replace(mask1, firstMetric).replace(mask2, secondMetric);
    }
    return ClientLevelKeywordTileSubtitles[filterKey].replace(mask1, firstMetric).replace(mask2, secondMetric);
  };

  const generateLowPerformanceSubtitle = (lowVolume: number, filterKey: KeywordFilter) => {
    const volumeMetric = metricFormatter(lowVolume, METRICTYPE.VOLUME, {
      showFullValue: false,
      decimalPlaces: 1
    });
    let subText = '';
    if (isProductEntity) {
      subText = ProductLevelKeywordTileSubtitles[filterKey].replace(mask1, volumeMetric);
    } else {
      subText = ClientLevelKeywordTileSubtitles[filterKey].replace(mask1, volumeMetric);
    }
    return (
      <TextArrow
        onClick={async () => {
          await scrollToId(ContentSectionId.TableGrid);
          resetFilters();
          if (filterKey === KeywordFilter.LowUsage) {
            setUsageBelow70(true);
          } else if (filterKey === KeywordFilter.LowProductivity) {
            setProductivityBelow70(true);
          } else if (filterKey === KeywordFilter.LowPopularity) {
            setPopularityBelow70(true);
          }
        }}
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          color: 'inherit',
          textTransform: 'none',
          padding: 0,
          minWidth: 'auto',
          justifyContent: 'flex-start',
          '&:hover': {
            backgroundColor: 'transparent !important',
            textDecoration: 'underline !important',
            cursor: 'pointer !important',
            '& .hover-arrow': {
              transform: 'translateX(5px)'
            }
          }
        }}
      >
        {subText}
      </TextArrow>
    );
  };

  const generateMetricSubtitle = (fullData: KeywordPerformanceGaugeData, filterKey: KeywordFilter) => {
    const item = fullData.gaugeData.find((item) => item.filterKey === filterKey);
    let subtitle = '';
    switch (filterKey) {
      case KeywordFilter.Usage:
      case KeywordFilter.KeywordBenchmark:
        subtitle = generateSingleMetricSubtitle(item, filterKey, {
          showFullValue: false,
          decimalPlaces: isProductEntity ? 0 : 1,
          valueMetricType: METRICTYPE.DECIMAL
        });
        break;
      case KeywordFilter.PopularityBenchmark:
        subtitle = generateSingleMetricSubtitle(item, filterKey, {
          showFullValue: false,
          decimalPlaces: 1,
          valueMetricType: METRICTYPE.VOLUME
        });
        break;
      case KeywordFilter.Productivity:
      case KeywordFilter.UnProductiveKeywords:
        subtitle = generateProductivitySubtitle(fullData, filterKey);
        break;
      case KeywordFilter.Popularity:
        subtitle = generatePopularitySubtitle(fullData, filterKey);
        break;
      case KeywordFilter.LowUsage:
        return generateLowPerformanceSubtitle(fullData.mainScores.productsWithLowKeywordUsageScore, filterKey);
      case KeywordFilter.LowProductivity:
        return generateLowPerformanceSubtitle(fullData.mainScores.productsWithLowKeywordProductivityScore, filterKey);
      case KeywordFilter.LowPopularity:
        return generateLowPerformanceSubtitle(fullData.mainScores.productsWithLowKeywordPopularityScore, filterKey);
      default:
        subtitle = '';
    }
    return <Text sx={{ fontSize: '14px', fontWeight: 400 }}>{subtitle}</Text>;
  };

  const getSplineChartSubtitle = ({
    formattedAverage,
    keywordFilter,
    formattedPercentChange,
    direction,
    keywordsUsed,
    isProductEntity = false
  }: {
    formattedAverage: string;
    keywordFilter: KeywordFilter;
    formattedPercentChange: string;
    direction: string;
    keywordsUsed?: string;
    isProductEntity?: boolean;
  }) => {
    let subtitleText: string;
    if (keywordFilter === KeywordFilter.Popularity) {
      if (isProductEntity) {
        const keywordFilterText = SplineChartSubtitleHeaders[keywordFilter]?.product;
        subtitleText = `On average, this product used ${keywordsUsed} ${keywordFilterText} on its page that generated ${formattedAverage} in average weekly search volume during the ${mainTimePeriodName} period, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodName}.`;
      } else {
        const keywordFilterText = SplineChartSubtitleHeaders[keywordFilter]?.default;
        subtitleText = `On average, ${entityName} used ${keywordsUsed} ${keywordFilterText} on each product page that generated ${formattedAverage} in average weekly search volume during the ${mainTimePeriodName} period, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodName}. `;
      }
    } else {
      if (isProductEntity) {
        const keywordFilterText = SplineChartSubtitleHeaders[keywordFilter]?.product;
        subtitleText = `On average, this product had ${formattedAverage} ${keywordFilterText} on its page during the ${mainTimePeriodName} period, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodName}.`;
      } else {
        const keywordFilterText = SplineChartSubtitleHeaders[keywordFilter]?.default;
        subtitleText = `On average, ${entityName} had ${formattedAverage} ${keywordFilterText} on each product page during the ${mainTimePeriodName} period, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodName}.`;
      }
    }

    return <Text>{subtitleText}</Text>;
  };

  return {
    generateMetricSubtitle,
    getSplineChartSubtitle
  };
};
